import { usageTagNamespaces } from "@erp/a2a-contract/v1/metadata";

/**
 * Classify and split raw tags coming back from the proxy.
 *
 * The namespace list is imported from the generated contract rather than re-declared, so
 * the read side cannot allowlist a namespace the write side never emits, or drop one it
 * does.
 */

/** The known namespaces, in canonical emission order. */
export const USAGE_TAG_NAMESPACES: readonly string[] = usageTagNamespaces;

const KNOWN = new Set<string>(usageTagNamespaces);

/**
 * Tags the proxy records that predate the current vocabulary.
 *
 * `backend` was the blanket default every backend request carried before the namespaced
 * scheme. It still exists in historical rows, so it is recognised and folded into a
 * synthetic `svc:backend` rather than reported as noise — otherwise every pre-migration day
 * shows its whole spend as "ignored".
 */
const LEGACY_TAGS: Record<string, { namespace: string; value: string }> = {
  backend: { namespace: "svc", value: "backend" },
};

export interface ParsedTag {
  tag: string;
  namespace: string;
  value: string;
}

/**
 * Split a tag into namespace and value, or return `null` if it is not ours.
 *
 * Splits on the **first** colon only. That matters for two reasons: `feature:job:reindex`
 * is a legitimate value containing a colon, and LiteLLM's own auto-added `User-Agent:
 * litellm/1.93.0` tag *contains* a colon — so a naive "has a colon therefore namespaced"
 * rule would classify it as a namespace called `User-Agent`. The allowlist is what actually
 * rejects it; splitting on the first colon just keeps values intact.
 */
export function parseTag(tag: string): ParsedTag | null {
  const legacy = LEGACY_TAGS[tag];
  if (legacy) return { tag, ...legacy };

  const separator = tag.indexOf(":");
  if (separator <= 0) return null;

  const namespace = tag.slice(0, separator);
  const value = tag.slice(separator + 1);
  if (!value || !KNOWN.has(namespace)) return null;

  return { tag, namespace, value };
}

/** Is this a tag we recognise and are willing to report on? */
export function isKnownTag(tag: string): boolean {
  return parseTag(tag) !== null;
}

export interface GroupedTags {
  /** Namespace → its tags, in the contract's namespace order. */
  byNamespace: Map<string, ParsedTag[]>;
  /** Everything the allowlist rejected — LiteLLM's own auto tags, and anything stale. */
  ignored: string[];
}

/**
 * Group raw tags by namespace, in canonical order, separating what we do not recognise.
 *
 * Order comes from the contract rather than from the proxy's response, so the UI's section
 * order is stable regardless of what the proxy happens to return first.
 */
export function groupTagsByNamespace(tags: readonly string[]): GroupedTags {
  const byNamespace = new Map<string, ParsedTag[]>();
  const ignored: string[] = [];

  for (const namespace of usageTagNamespaces) byNamespace.set(namespace, []);
  // `svc` is synthetic — it exists only to hold folded legacy tags, so it is not in the
  // emitted vocabulary but must still have a bucket.
  byNamespace.set("svc", []);

  for (const tag of tags) {
    const parsed = parseTag(tag);
    if (!parsed) {
      ignored.push(tag);
      continue;
    }
    byNamespace.get(parsed.namespace)?.push(parsed);
  }

  for (const [namespace, parsed] of byNamespace) {
    if (parsed.length === 0) byNamespace.delete(namespace);
  }

  return { byNamespace, ignored };
}

/**
 * Split a LiteLLM key alias into its environment and service halves.
 *
 * `add-env.sh` mints `<env-slug>-<backend|ai-service>`, which is how the read side recovers
 * environment and service without either being a tag — deriving them from the key that
 * actually authenticated means they cannot drift from reality.
 *
 * Deliberately not reusing the control plane's `splitAlias`: that parses *team* aliases
 * (`<slug>` and `<slug>-ai-service`), a different convention, and applying it here would
 * mis-split every backend key.
 */
export function splitKeyAlias(keyAlias: string): {
  environment: string;
  service: string;
} {
  for (const service of ["ai-service", "backend"]) {
    const suffix = `-${service}`;
    if (keyAlias.endsWith(suffix)) {
      return { environment: keyAlias.slice(0, -suffix.length), service };
    }
  }
  return { environment: keyAlias, service: "unknown" };
}
